import type { YearBucket } from '@finsim/charts';
import type { Project } from '@finsim/model';
import { displayOrder, itemGroup, type ItemGroup } from '../../../entities/project';
import type { ScenarioResult } from '../../../entities/simulation';
import type { ValueMode } from '../../../entities/view-settings';

export interface LedgerRow {
  id: string;
  label: string;
  group: ItemGroup | 'overdraft' | 'total' | 'balance';
  /** Flows are summed over a period; balances take the period's last month. */
  kind: 'flow' | 'balance';
  values: ArrayLike<number>;
  /** A part of the row above (e.g. a tax's contributions); not added to totals. */
  parent?: string;
}

export interface LedgerColumn {
  key: string;
  from: number;
  to: number;
}

export interface LedgerLabels {
  overdraft: string;
  incomeTax: string;
  netCashFlow: string;
  cash: string;
  assets: string;
  liabilities: string;
  netWorth: string;
}

/** Rows of the ledger for one variant: item cash flows, overdraft, net cash flow, balances. */
export function ledgerRows(
  project: Project,
  scenarioId: string,
  result: ScenarioResult,
  mode: ValueMode,
  labels: LedgerLabels,
): LedgerRow[] {
  const scenario = project.scenarios.find((s) => s.id === scenarioId);
  const rows: LedgerRow[] = [];
  if (scenario) {
    for (const item of displayOrder(project, scenario.id)) {
      const series = result.items[item.id];
      if (!series) continue;
      const group = itemGroup(item);
      rows.push({ id: item.id, label: item.name, group, kind: 'flow', values: series.cash[mode] });
      if (
        series.type === 'flow' &&
        series.tax &&
        item.type === 'formula' &&
        item.spec.kind === 'tax'
      ) {
        // Split taxes that also pay contributions into their parts.
        const paid = item.spec.deductions.filter((d) => d.emitAsFlow);
        if (paid.length === 0) continue;
        const { tax } = series;
        const detail = (id: string, label: string, values: ArrayLike<number>): LedgerRow => ({
          id: `${item.id}:${id}`,
          label,
          group,
          kind: 'flow',
          values: Array.from(values, (v) => -v),
          parent: item.name,
        });
        for (const d of paid) {
          const values = tax.deductions[d.id]?.[mode];
          if (values) rows.push(detail(d.id, d.name, values));
        }
        rows.push(detail('tax', labels.incomeTax, tax.tax[mode]));
      }
    }
  }
  const overdraft = result.series.overdraftInterest[mode].map((v) => -v);
  if (overdraft.some((v) => v !== 0))
    rows.push({
      id: 'overdraft',
      label: labels.overdraft,
      group: 'overdraft',
      kind: 'flow',
      values: overdraft,
    });
  const net = new Float64Array(result.series.cash[mode].length);
  for (const r of rows) {
    if (r.parent !== undefined) continue;
    for (let i = 0; i < net.length; i++) net[i] = (net[i] ?? 0) + (r.values[i] ?? 0);
  }
  rows.push({ id: 'net', label: labels.netCashFlow, group: 'total', kind: 'flow', values: net });
  rows.push(
    {
      id: 'cash',
      label: labels.cash,
      group: 'balance',
      kind: 'balance',
      values: result.series.cash[mode],
    },
    {
      id: 'assets',
      label: labels.assets,
      group: 'balance',
      kind: 'balance',
      values: result.series.assets[mode],
    },
    {
      id: 'liabilities',
      label: labels.liabilities,
      group: 'balance',
      kind: 'balance',
      values: result.series.liabilities[mode],
    },
    {
      id: 'netWorth',
      label: labels.netWorth,
      group: 'balance',
      kind: 'balance',
      values: result.series.netWorth[mode],
    },
  );
  return rows;
}

/** Year columns, with expanded years split into months. */
export function ledgerColumns(
  buckets: readonly YearBucket[],
  expanded: ReadonlySet<number>,
): LedgerColumn[] {
  return buckets.flatMap((b) =>
    expanded.has(b.year)
      ? Array.from({ length: b.to - b.from + 1 }, (_, k) => ({
          key: `${String(b.year)}-${String(k)}`,
          from: b.from + k,
          to: b.from + k,
        }))
      : [{ key: String(b.year), from: b.from, to: b.to }],
  );
}

export function cellValue(row: LedgerRow, column: LedgerColumn): number {
  if (row.kind === 'balance') return row.values[column.to] ?? 0;
  let sum = 0;
  for (let i = column.from; i <= column.to; i++) sum += row.values[i] ?? 0;
  return sum;
}

/** RFC 4180 CSV with one column per month (raw numbers, no formatting). */
export function ledgerCsv(rows: readonly LedgerRow[], monthLabels: readonly string[]): string {
  const escape = (s: string): string => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const header = ['', ...monthLabels].map(escape).join(',');
  const lines = rows.map((r) =>
    [
      escape(r.parent === undefined ? r.label : `${r.parent} – ${r.label}`),
      ...monthLabels.map((_, i) => String(Math.round((r.values[i] ?? 0) * 100) / 100)),
    ].join(','),
  );
  return [header, ...lines].join('\n') + '\n';
}
